import * as Api from '@ViewModels';
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { LevStandardQueryMeta } from '../../../web/ReactQueryProvider';
import { IAuditLogEntry, IAuditLogFilters } from './types';

export const ADMIN_AUDIT_LOG_QUERY_KEY = 'admin/account/audit-log';

const DEFAULT_PAGE_SIZE = 50;

/** Default lookback window the event log opens with. */
export const DEFAULT_AUDIT_LOG_LOOKBACK_DAYS = 30;

/** Maximum retention window the API stores events for. Single source of truth across query + UI. */
export const AUDIT_LOG_RETENTION_DAYS = 60;

/**
 * The audit log is append-mostly historical data, so brief staleness is acceptable. A non-zero
 * staleTime means re-opening the section (or refocusing the window) within this window serves the
 * already-loaded rows instantly instead of triggering another network round-trip.
 */
export const ADMIN_AUDIT_LOG_STALE_TIME = 60_000;

/** Minimal shape of the web service helper used to issue the request (from the user session). */
interface IAuditLogWebServiceHelper {
	callAsync<T>(endpoint: string, method: string): Promise<T>;
}

function buildAuditLogUrl(accountId: string, filters: IAuditLogFilters, pageSize: number, pageToken: string): string {
	const params = new URLSearchParams();
	if (filters.startDate && filters.endDate) {
		params.set('startDate', filters.startDate);
		params.set('endDate', filters.endDate);
	} else {
		params.set('lookbackDays', String(filters.lookbackDays));
	}
	params.set('pageSize', String(pageSize));
	if (filters.contactId) {
		params.set('contactId', filters.contactId);
	}
	if (filters.category) {
		params.set('category', filters.category);
	}
	if (pageToken) {
		params.set('pageToken', pageToken);
	}
	return `admin/account/${encodeURIComponent(accountId)}/audit-log?${params.toString()}`;
}

/**
 * Infinite-query options for the account audit log, factored out of the hook so the query key and
 * fetcher live in one place (and can be exercised directly in tests).
 */
export function buildAdminAccountAuditLogQueryOptions({
	accountId,
	filters,
	pageSize = DEFAULT_PAGE_SIZE,
	webServiceHelper,
}: {
	accountId: string;
	filters: IAuditLogFilters;
	pageSize?: number;
	webServiceHelper: IAuditLogWebServiceHelper;
}) {
	return infiniteQueryOptions<Api.IPagedCollection<IAuditLogEntry>>({
		// Treat an empty-string or null pageToken as "no more pages" — without this, a defined-but-empty token would loop forever.
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		initialPageParam: '',
		queryFn: ({ pageParam }) =>
			webServiceHelper.callAsync<Api.IPagedCollection<IAuditLogEntry>>(
				buildAuditLogUrl(accountId, filters, pageSize, pageParam as string),
				'GET'
			),
		queryKey: [
			ADMIN_AUDIT_LOG_QUERY_KEY,
			accountId,
			filters.contactId ?? null,
			filters.category ?? null,
			filters.startDate && filters.endDate ? 'custom' : filters.lookbackDays,
			filters.startDate ?? null,
			filters.endDate ?? null,
			pageSize,
		],
		staleTime: ADMIN_AUDIT_LOG_STALE_TIME,
	});
}

export function useAdminAccountAuditLogQuery({
	accountId,
	filters,
	pageSize = DEFAULT_PAGE_SIZE,
	enabled = true,
	meta,
}: {
	accountId: string;
	filters: IAuditLogFilters;
	pageSize?: number;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useInfiniteQuery({
		...buildAdminAccountAuditLogQueryOptions({
			accountId,
			filters,
			pageSize,
			webServiceHelper: userSession.webServiceHelper,
		}),
		enabled,
		meta,
	});
}
